import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KeyRound, Copy, X } from "lucide-react";

const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });

export default function FamilyInvites({ patientId, patientName }) {
  const [codes, setCodes] = useState([]);
  const load = useCallback(() => api.get(`/patients/${patientId}/invites`).then((r) => setCodes(r.data)).catch(() => {}), [patientId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try { await api.post(`/patients/${patientId}/invites`); load(); toast.success("Code d'invitation créé"); }
    catch { toast.error("Création impossible."); }
  };
  const copy = (c) => {
    const text = `Bonjour, voici votre code pour suivre ${patientName} sur Passerelle : ${c}\nCréez votre accès sur ${window.location.origin}/rejoindre`;
    navigator.clipboard?.writeText(text).then(() => toast.success("Invitation copiée, vous pouvez la coller dans un SMS ou un e-mail"))
      .catch(() => toast(c));
  };
  const revoke = async (c) => {
    await api.delete(`/patients/${patientId}/invites/${c}`).catch(() => {});
    load();
  };

  return (
    <div className="rounded-2xl bg-sky-50 border border-sky-200 p-4 mb-4" data-testid="family-invites">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-700 max-w-md">
          <b>Code d'invitation</b> : donnez-le au proche. Il crée lui-même son accès sur la page « Rejoindre ». Chaque code ne sert qu'une fois et reste valable 14 jours.
        </p>
        <Button size="sm" variant="outline" onClick={create} className="rounded-full" data-testid="create-invite-button">
          <KeyRound className="w-4 h-4 mr-1" /> Générer un code
        </Button>
      </div>
      {codes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {codes.map((c) => (
            <span key={c.code} className="inline-flex items-center gap-2 rounded-full bg-white border border-sky-200 pl-4 pr-1 h-10" data-testid={`invite-${c.code}`}>
              <b className="tracking-[0.25em] text-gray-800" data-testid="invite-code">{c.code}</b>
              <span className="text-xs text-gray-400">jusqu'au {fmt(c.expires_at)}</span>
              <button onClick={() => copy(c.code)} className="w-8 h-8 rounded-full hover:bg-sky-50 flex items-center justify-center" aria-label="Copier" data-testid={`invite-copy-${c.code}`}><Copy className="w-4 h-4" /></button>
              <button onClick={() => revoke(c.code)} className="w-8 h-8 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 flex items-center justify-center" aria-label="Annuler" data-testid={`invite-revoke-${c.code}`}><X className="w-4 h-4" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
